<template>
  <SeatingDataProvider
    v-slot="{ mySeat, guestSeats, hasGuests, hasSeating, loading, failed }"
  >
    <!-- Same rule as SeatingInfo: nothing on events without a seating plan, and
         nothing while the request is in flight or after it failed — an empty
         answer and a failed one look alike, and only the first may be shown as
         "not assigned yet". -->
    <section
      v-if="hasSeating && !loading && !failed"
      class="seating-card"
      :aria-label="$t('Views.Stage.MyProfile.seatingArrangement')"
    >
      <div class="seating-row">
        <div class="seating-row__icon seating-row__icon--primary">
          <i class="fas fa-chair" aria-hidden="true"></i>
        </div>
        <div class="seating-row__text">
          <span class="seating-row__title">{{
            $t('Views.Stage.MyProfile.seatingArrangement')
          }}</span>
          <span
            class="seating-row__desc"
            :class="{ 'seating-row__desc--empty': !isAssigned(mySeat) }"
            v-text="seatText(mySeat)"
          />
        </div>
      </div>

      <div v-if="hasGuests" class="seating-guests">
        <span class="seating-guests__label">{{
          $t('Views.Stage.MyProfile.myGuests')
        }}</span>
        <div
          v-for="(guest, index) in guestSeats"
          :key="index"
          class="seating-row seating-row--guest"
        >
          <div class="seating-row__icon seating-row__icon--guest">
            <i class="far fa-user" aria-hidden="true"></i>
          </div>
          <div class="seating-row__text">
            <span class="seating-row__title" v-text="guestName(guest, index)" />
            <span
              class="seating-row__desc"
              :class="{ 'seating-row__desc--empty': !isAssigned(guest) }"
              v-text="seatText(guest)"
            />
          </div>
        </div>
      </div>
    </section>
  </SeatingDataProvider>
</template>

<script>
import seatingFormat from '@/mixins/seatingFormat';

export default {
  name: 'SeatingCard',
  mixins: [seatingFormat],
  methods: {
    // Lets the "not assigned yet" placeholder be muted instead of looking like
    // a real seat.
    isAssigned(seat) {
      return !!(seat?.locationName || seat?.seatNumber);
    }
  }
};
</script>

<style scoped lang="scss">
// Same surface as the Profile card and the Event Badge / Vouchers tiles on
// ProfilePage, so the seat reads as one more block of that page.
.seating-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 640px) {
    margin-bottom: 22px;
  }
}

.seating-row {
  display: flex;
  align-items: center;
  gap: 14px;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;

    &--primary {
      background: #e6f7f5;
      color: var(--color-primary, #047873);
    }
    &--guest {
      background: #f3f0ff;
      color: #7c3aed;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    word-break: break-word;
  }

  &__desc {
    font-size: 13px;
    color: #374151;
    margin-top: 2px;
    word-break: break-word;

    &--empty {
      color: #6b7280;
    }
  }

  // Guests sit a step below the participant's own seat.
  &--guest {
    & + & {
      margin-top: 12px;
    }

    .seating-row__icon {
      width: 38px;
      height: 38px;
      font-size: 14px;
    }
    .seating-row__title {
      font-size: 14px;
    }
  }
}

.seating-guests {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;

  &__label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
    margin-bottom: 12px;
  }
}

body[data-theme='dark'] {
  .seating-card {
    background: var(--t-surface);
    border-color: var(--t-border);
    box-shadow: var(--t-card-shadow, 0 1px 4px rgba(0, 0, 0, 0.3));
  }
  .seating-row__title {
    color: var(--t-text);
  }
  .seating-row__desc {
    color: var(--t-text-secondary);

    &--empty {
      color: var(--t-text-muted);
    }
  }
  .seating-guests {
    border-top-color: var(--t-border);
  }
  .seating-guests__label {
    color: var(--t-text-muted);
  }
}
</style>
